import { useEffect, useRef, useState } from 'react'
import { Kbd } from './glyphs'
import { addRule, failureText, removeRule, type Rule } from './api'
import { store, type Conversation } from './store'

// Free mail domains: a domain rule would catch everyone on Gmail.
const PERSONAL = /^(gmail|googlemail|icloud|me|mac|outlook|hotmail|live|msn|yahoo|ymail|aol|proton|protonmail|pm|fastmail|hey|gmx|zoho)\./

/**
 * "m": tell Jev, in your own words, what mail like this is. The prompt is the
 * whole palette; one-key shortcuts for "just this sender" sit underneath.
 */
export function Teach({ item, onClose, onDone }: { item: Conversation; onClose: () => void; onDone: (text: string, undo?: () => Promise<unknown>) => void }) {
  const from = item.lead.from
  const email = from.email.toLowerCase()
  const domain = email.split('@')[1] ?? ''
  const name = from.name || email
  const inOther = item.decision ? !item.decision.important : false
  const [action, setAction] = useState<Rule['action']>(inOther ? 'important' : 'other')
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [closing, setClosing] = useState(false)
  const input = useRef<HTMLTextAreaElement>(null)
  const where = action === 'other' ? 'Other' : 'Important'
  const canDomain = !!domain && !PERSONAL.test(domain)

  useEffect(() => { input.current?.focus() }, [])

  // Leave with a short exit instead of vanishing.
  const close = (after?: () => void) => {
    setClosing(true)
    setTimeout(() => { after?.(); onClose() }, 140)
  }

  async function save(rule: Pick<Rule, 'kind' | 'pattern' | 'action'> & { label?: string }, message: string) {
    setBusy(true); setError('')
    try {
      const { rule: saved } = await addRule(rule)
      store.soon(50)
      close(() => onDone(message, async () => { await removeRule(saved.id); store.soon(50) }))
    } catch (failure) { setError(failureText(failure)); setBusy(false) }
  }
  const teach = () => text.trim().length >= 8 && save({ kind: 'text', pattern: text.trim(), action }, `Got it. Re-sorting your mail…`)
  const sender = () => save({ kind: 'sender', pattern: email, action, label: action === 'other' ? name : 'Important' }, `${name} goes to ${where} from now on.`)
  const wholeDomain = () => save({ kind: 'domain', pattern: domain, action, label: action === 'other' ? domain : 'Important' }, `Everything from ${domain} goes to ${where}.`)

  function onKey(event: React.KeyboardEvent) {
    event.stopPropagation()
    if (event.key === 'Escape') { event.preventDefault(); close() }
    else if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void teach() }
    else if (event.key === 'Tab') { event.preventDefault(); setAction(action === 'other' ? 'important' : 'other') }
    else if (event.altKey && event.code === 'Digit1') { event.preventDefault(); void sender() }
    else if (event.altKey && event.code === 'Digit2' && canDomain) { event.preventDefault(); void wholeDomain() }
  }

  return (
    <div className={`overlay${closing ? ' closing' : ''}`} onMouseDown={event => { if (event.target === event.currentTarget) close() }}>
      <div className="palette teach" role="dialog" aria-label="Teach Jev" onKeyDown={onKey}>
        <div className="teach-context">
          <span className="ellipsis">{name} · {item.subject}</span>
          <button className={`teach-direction ${action}`} onClick={() => setAction(action === 'other' ? 'important' : 'other')} type="button" title="Tab to switch">
            <span key={action} className="swap">{action === 'other' ? 'Move to Other' : 'Move to Important'}</span><Kbd k="tab" />
          </button>
        </div>
        <textarea ref={input} className="teach-input" rows={2} value={text} onChange={event => { setText(event.target.value); setError('') }}
          placeholder={action === 'other' ? 'What is mail like this? e.g. cold pitches sent to my Inbound support addresses' : 'What makes this matter? e.g. anything about my lease or apartment'} />
        <div className="teach-foot">
          <button className="chip" onClick={() => void sender()} disabled={busy} type="button"><Kbd k="⌥1" /><span className="chip-name">Just {name}</span><span className={`chip-dest ${action}`}>→ {where}</span></button>
          {canDomain && <button className="chip" onClick={() => void wholeDomain()} disabled={busy} type="button"><Kbd k="⌥2" /><span className="chip-name">All of {domain}</span><span className={`chip-dest ${action}`}>→ {where}</span></button>}
          <span className={`teach-send${text.trim().length >= 8 ? ' ready' : ''}`}>{busy ? <span className="spinner" /> : <Kbd k="↵" />}Teach Jev</span>
        </div>
        {error && <div className="warn small palette-error">{error}</div>}
      </div>
    </div>
  )
}
